import React, { useMemo } from 'react';
import {
	from,
	ApolloClient,
	ApolloProvider,
	ApolloConsumer,
	InMemoryCache,
} from '@apollo/client';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { onError } from '@apollo/client/link/error';

import UniversalOnboardingApp from './UniversalOnboardingApp';
import {
	getCookie,
	isNotProd,
	checkExpired,
	setCookie,
	formatAccessToken,
} from 'stash/utils';
import {
	ACCESS_TOKEN_COOKIE_NAME,
	USER_ID_COOKIE_NAME,
	UUID_COOKIE_NAME,
	REFRESH_TOKEN_COOKIE_NAME,
} from 'stash/constants/session';
import { logOut } from 'stash/actions/session';
import { refreshTokenRequest } from 'stash/actions/api/auth';
import { useStore } from 'react-redux';
import { isFBorIGWebView } from 'stash/utils/mobile';
import { BustingWebView } from 'stash/components/global';
const GRAPHQL_401_ERROR_RESPONSE = '401: Unauthorized';
const GRAPHQL_401_SMAUG_ERROR_RESPONSE = 'Unable to validate JWT.';
const GRAPHQL_406_SMAUG_ERROR_RESPONSE =
	'API request was blocked because it is determined to be malicious';

const httpLink = createHttpLink({
	uri: `/sign-up/graphql`,
});

function createAuthMiddleware(store) {
	const user_id = getCookie(USER_ID_COOKIE_NAME);
	const user_uuid = getCookie(UUID_COOKIE_NAME);
	const refreshToken = getCookie(REFRESH_TOKEN_COOKIE_NAME);
	let authorization = decodeURIComponent(getCookie(ACCESS_TOKEN_COOKIE_NAME));

	return new ApolloLink((operation, forward) => {
		// check to see if the access token is about to expire and refresh it if so
		if (refreshToken && checkExpired(authorization)) {
			store.dispatch(refreshTokenRequest(refreshToken)).then((res) => {
				authorization = formatAccessToken(res.access_token);
				setCookie(REFRESH_TOKEN_COOKIE_NAME, res.refresh_token);
			});
		}

		if (authorization && user_uuid) {
			operation.setContext({
				headers: {
					user_id,
					user_uuid,
					authorization,
				},
			});
		}
		return forward(operation);
	});
}

function createErrorLink(store) {
	return onError(({ graphQLErrors, networkError }) => {
		if (graphQLErrors) {
			graphQLErrors.forEach(({ message, locations, path }) => {
				console.error(
					`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
				);

				if (
					message === GRAPHQL_401_SMAUG_ERROR_RESPONSE ||
					message === GRAPHQL_401_ERROR_RESPONSE ||
					message === GRAPHQL_406_SMAUG_ERROR_RESPONSE
				) {
					store.dispatch(logOut());
				}
			});
			if (networkError) {
				console.error(`[Network error]: ${networkError}`);
			}
		}
	});
}

function createApolloClient(store) {
	return new ApolloClient({
		link: from([createErrorLink(store), createAuthMiddleware(store).concat(httpLink)]),
		cache: new InMemoryCache(),
		connectToDevTools: isNotProd(),
	});
}

function UniversalOnboardingAppWithGraphql() {
	const store = useStore();
	const apolloClient = useMemo(() => createApolloClient(store), [store]);
	const uuid = getCookie(UUID_COOKIE_NAME);

	return (
		<div>
			{!uuid && isFBorIGWebView() && <BustingWebView />}
			<ApolloProvider client={apolloClient}>
				<ApolloConsumer>
					{(client) => <UniversalOnboardingApp client={client} />}
				</ApolloConsumer>
			</ApolloProvider>
		</div>
	);
}

export default UniversalOnboardingAppWithGraphql;
